import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import { UserService } from '../services/user.service';
import { User } from '../../models/user.model';
import { DatePipe } from '@angular/common';
import { Invitation } from '../../models/invitation.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { isPermissionError, parseApiError } from '../quotes-api-error';
import { Settings } from '../../models/settings.model';
import { firstValueFrom, Subscription } from 'rxjs';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import {
  InviteMemberDialogComponent,
  RoleOption,
} from './invite-member-dialog/invite-member-dialog.component';
import { TenantMembershipRole } from '@tangible/shared';

@Component({
  selector: 'app-team-page',
  imports: [
    ButtonModule,
    ConfirmDialogModule,
    DatePipe,
    FormsModule,
    SelectModule,
    Tab,
    TabList,
    Tabs,
    TooltipModule,
    InviteMemberDialogComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './team-page.component.html',
  styleUrl: './team-page.component.scss',
})
export class TeamPageComponent implements OnInit, OnDestroy {
  settingsService = inject(SettingsService);
  userService = inject(UserService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);

  currentUser: User | undefined;
  usersInAccount: User[] = [];
  invitations: Invitation[] = [];
  settings: Settings | null = null;
  currentSeats = 0;
  maxSeats = 3; // Default fallback
  activeTab = signal<'current' | 'pending'>('current');
  inviteDialogVisible = signal(false);

  get sortedUsersInAccount(): User[] {
    return [...this.usersInAccount]
      .filter(
        (u) => u.role !== 'superuser' || this.currentUser?.role === 'superuser',
      )
      .sort((a, b) => {
        if (a.id === this.currentUser?.id) return -1;
        if (b.id === this.currentUser?.id) return 1;
        return 0;
      });
  }

  get canManageTeam(): boolean {
    return (
      this.currentUser?.role === 'admin' ||
      this.currentUser?.role === 'superuser'
    );
  }

  get adminOnlyTooltip(): string {
    return this.canManageTeam ? '' : 'Only admins can manage the team.';
  }

  get isSeatsFull(): boolean {
    return this.currentSeats >= this.maxSeats;
  }

  /** Superusers never consume a seat, so only they can invite at capacity. */
  get inviteDisabled(): boolean {
    return this.isSeatsFull && this.currentUser?.role !== 'superuser';
  }

  get roleOptions(): RoleOption[] {
    const options: RoleOption[] = [
      { label: 'Viewer', value: 'viewer' },
      { label: 'Creator', value: 'creator' },
      { label: 'Approver', value: 'approver' },
      { label: 'Admin', value: 'admin' },
    ];
    if (this.currentUser?.role === 'superuser') {
      options.push({ label: 'Superuser', value: 'superuser' });
    }
    return options;
  }

  /** At capacity, only seat-free superuser invites remain. */
  get inviteRoleOptions(): RoleOption[] {
    if (this.isSeatsFull) {
      return this.roleOptions.filter((option) => option.value === 'superuser');
    }
    return this.roleOptions;
  }

  private subscriptions = new Subscription();

  ngOnInit() {
    this.userService.loadCurrentUser().then((user) => {
      this.currentUser = user ?? undefined;
      this.refreshUsersInAccount();
      this.refreshInvitations();
    });

    // Get settings to get max_seats
    this.subscriptions.add(
      this.settingsService.settings$.subscribe((settings) => {
        this.settings = settings;
        if (settings?.max_seats) {
          this.maxSeats = settings.max_seats;
        }
        this.updateSeatCount();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updateSeatCount() {
    // Listed users are already the active team; exclude superusers from seats.
    const activeUsers = this.usersInAccount.filter(
      (user) => user.role !== 'superuser',
    ).length;
    const pendingInvitations = this.invitations.filter(
      (inv) => !inv.accepted_at && !inv.revoked_at && inv.role !== 'superuser',
    ).length;
    this.currentSeats = activeUsers + pendingInvitations;
  }

  async changeUserRole(user: User, newRole: string) {
    try {
      await this.userService.patchUser$(user, { role: newRole });
      this.refreshUsersInAccount();
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Role updated for ${user.email}`,
      });
    } catch (error: unknown) {
      if (isPermissionError(error)) return;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: parseApiError(error, 'Failed to update user role'),
      });
      throw error;
    }
  }

  async sendInvite({
    email,
    role,
  }: {
    email: string;
    role: TenantMembershipRole;
  }) {
    try {
      await firstValueFrom(this.userService.createInvitation$(email, role));
      this.inviteDialogVisible.set(false);
      this.activeTab.set('pending');
      this.refreshInvitations();
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Invitation sent to ${email}`,
      });
    } catch (error: unknown) {
      if (isPermissionError(error)) return;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: parseApiError(error, 'Failed to send invitation'),
      });
      throw error;
    }
  }

  refreshInvitations() {
    this.subscriptions.add(
      this.userService.getInvitations$().subscribe((invites) => {
        this.invitations = invites;
        this.updateSeatCount();
      }),
    );
  }

  confirmRevokeInvitation(invitation: Invitation) {
    this.confirmationService.confirm({
      header: 'Revoke invitation',
      message: `Revoke the invitation for ${invitation.email}? The invite link will stop working.`,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Revoke',
        severity: 'danger',
      },
      accept: () => this.revokeInvitation(invitation.token),
    });
  }

  confirmRemoveUser(user: User) {
    this.confirmationService.confirm({
      header: 'Remove member',
      message: `Remove ${user.email} from this team? They will lose access immediately.`,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Remove',
        severity: 'danger',
      },
      accept: () => this.removeUser(user),
    });
  }

  confirmLeaveAccount() {
    this.confirmationService.confirm({
      header: 'Leave account',
      message:
        'Leave this account? You will lose access immediately and need a new invitation to rejoin.',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Leave Account',
        severity: 'danger',
      },
      accept: () => this.leaveAccount(),
    });
  }

  resendInvitation(invitation: Invitation) {
    this.subscriptions.add(
      this.userService.resendInvitation$(invitation.token).subscribe({
        next: () => {
          this.refreshInvitations();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Invitation resent to ${invitation.email}`,
          });
        },
        error: (error: unknown) => {
          if (isPermissionError(error)) return;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: parseApiError(error, 'Failed to resend invitation'),
          });
        },
      }),
    );
  }

  revokeInvitation(token: string) {
    this.subscriptions.add(
      this.userService.revokeInvitation$(token).subscribe(() => {
        this.refreshInvitations();
        this.updateSeatCount();
      }),
    );
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Invitation revoked',
    });
  }

  refreshUsersInAccount() {
    this.subscriptions.add(
      this.userService.getUsers$().subscribe((users) => {
        this.usersInAccount = users;
        this.updateSeatCount();
      }),
    );
  }

  leaveAccount() {
    if (!this.currentUser) return;
    this.userService
      .deleteUser$(this.currentUser)
      .then(() => {
        // Refresh the page now to force logout
        window.location.reload();
      })
      .catch((error: unknown) => {
        if (isPermissionError(error)) return;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: parseApiError(error, 'Failed to leave account'),
        });
        throw error;
      });
  }

  removeUser(user: User) {
    this.userService
      .deleteUser$(user)
      .then(() => {
        this.refreshUsersInAccount();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `User ${user.email} removed`,
        });
      })
      .catch((error: unknown) => {
        if (isPermissionError(error)) return;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: parseApiError(error, 'Failed to remove user'),
        });
        throw error;
      });
  }
}
